import { AxiosError } from 'axios';
import type { AxiosInstance } from 'axios';
import NavigationService from '../../../services/navigationService';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { ROUTES } from '../../constants/routes';
import { STORAGE_KEYS } from '@/constants/storage';
import { clearAllFilterCaches } from '@/lib/filterCache';
import { clearAllAppCookies } from '@/lib/cookies';
import { getTimeUntilExpiration } from '@/utils/jwtUtils';

const AUTH_RETRY_WINDOW_MS = 30_000;
const AUTH_MAX_401_BEFORE_ERROR = 3;

const record401AndShouldEscalate = (): boolean => {
  try {
    const now = Date.now();
    const lastAt = Number(sessionStorage.getItem('auth:last401At') || '0');
    const prevCount = Number(sessionStorage.getItem('auth:401Count') || '0');

    const withinWindow = lastAt && now - lastAt <= AUTH_RETRY_WINDOW_MS;
    const nextCount = withinWindow ? prevCount + 1 : 1;

    sessionStorage.setItem('auth:last401At', String(now));
    sessionStorage.setItem('auth:401Count', String(nextCount));

    return nextCount >= AUTH_MAX_401_BEFORE_ERROR;
  } catch {
    // If sessionStorage is unavailable, be conservative and escalate
    return true;
  }
};

// Request interceptor
export const setupRequestInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(STORAGE_KEYS.GROM_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        
        // Proactive token refresh check: If token is expiring soon (< 2 minutes),
        // trigger refresh immediately (fire and forget) to prevent request failures
        try {
          const timeUntilExpiration = getTimeUntilExpiration(token);
          const twoMinutesInMs = 2 * 60 * 1000;
          
          // If token expires in less than 2 minutes, trigger refresh proactively
          if (timeUntilExpiration > 0 && timeUntilExpiration < twoMinutesInMs) {
            // Dispatch custom event to trigger token refresh in AuthContext
            // This is fire-and-forget, doesn't block the current request
            window.dispatchEvent(new CustomEvent('token:refresh-required', {
              detail: { reason: 'proactive-refresh', timeUntilExpiration }
            }));
            console.log(`Token expiring in ${Math.round(timeUntilExpiration / 1000)}s, triggering proactive refresh`);
          }
        } catch (error) {
          // If token parsing fails, silently continue - let the request proceed
          // The response interceptor will handle 401 errors
          console.warn('Failed to check token expiration in request interceptor:', error);
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

// Response interceptor
export const setupResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => {
      return response;
    },
    (error: AxiosError) => {
      if (error.response) {
        const currentPath = window.location.pathname;
        const isTokenEndpoint = error.config?.url?.includes(API_ENDPOINTS.AUTH.TOKEN);
        
        // Token endpoint errors should be handled by AuthContext/TokenService, not interceptor
        // to avoid double navigation and conflicts. Only handle non-token endpoint errors here.
        if (isTokenEndpoint) {
          // Let the error propagate to TokenService/AuthContext for proper handling
          // They have retry logic and know the context better
          return Promise.reject(error);
        }

        switch (error.response.status) {
          case 401: {
            // Clear auth-related storage and caches
            try {
              localStorage.removeItem(STORAGE_KEYS.GROM_TOKEN);
              localStorage.removeItem(STORAGE_KEYS.ACCOUNT_INFO);
              clearAllFilterCaches();
              clearAllAppCookies();
            } catch {
              // ignore storage errors
            }
            // Only 401/403 lead to auth-error; 401 may go to home for re-auth first
            const shouldEscalateToError = record401AndShouldEscalate();
            const target = shouldEscalateToError ? ROUTES.AUTH.ERROR : ROUTES.HOME;

            if (currentPath !== target) {
              NavigationService.navigateTo(target);
            }
            break;
          }
          case 403:
            // 403 = authenticated but lacks permission → auth-error
            if (currentPath !== ROUTES.AUTH.ERROR) {
              NavigationService.navigateTo(ROUTES.AUTH.ERROR);
            }
            break;
          case 404:
            console.error('Resource not found');
            break;
          default:
            // Other statuses (400, 500, etc.): log only, no navigation
            console.error('API error:', error.response?.status, error.message);
            break;
        }
      } else if (error.request) {
        console.error('Unable to connect to server');
      } else {
        console.error('Error:', error.message);
      }
      return Promise.reject(error);
    }
  );
};
